<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Stepper from 'primevue/stepper';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { FlexScroller, SplitBar, Toolbar, Typography } from '@apparatix/library/core';
import { button as greenButtonTheme } from '@apparatix/theme/Green';
import { useContactMerge, useToastNotifications } from '@apparatix/composables';
import {
  type MergeCandidate,
  type MergeChoice,
  type MergeFieldKey,
  type MergeNoteChoice,
  type MergePayload,
  type MergePreview,
} from '@apparatix/types/Contacts';
import MergeCandidateList from './MergeCandidateList.vue';
import MergeFieldPicker from './MergeFieldPicker.vue';
import MergeReview from './MergeReview.vue';
import {
  buildMergeValueOptions,
  mergeFieldDefinitions,
  mergeLockedFieldKeys,
  normalizeEmail,
  normalizePhoneNumber,
  normalizeUrl,
  type MergeSummary,
} from './mergeWizardHelpers';

type Props = {
  sourceContactId: number;
  sourceContactName?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  merged: [];
}>();

const {
  getMergeCandidates, getMergePreview, mergeContacts, isLoadingCandidates, isLoadingPreview, isMerging,
} = useContactMerge();
const { showWarning } = useToastNotifications();
const confirm = useConfirm();

const activeStep = ref(1);
const candidates = ref<MergeCandidate[]>([]);
const preview = ref<MergePreview | null>(null);

/* eslint-disable camelcase -- keys match the backend crm_contacts column names */
const defaultFieldChoices = (): Record<MergeFieldKey, MergeChoice> => ({
  salutation: 'source',
  first_name: 'source',
  middle_name: 'source',
  last_name: 'source',
  nickname: 'source',
  suffix: 'source',
  job_title: 'source',
  company_name: 'source',
  birthday: 'source',
  owner: 'source',
});
/* eslint-enable camelcase */

const fieldChoices = ref<Record<MergeFieldKey, MergeChoice>>(defaultFieldChoices());
const addressChoice = ref<MergeChoice>('source');
const keptEmailIds = ref<number[]>([]);
const keptPhoneNumberIds = ref<number[]>([]);
const keptWebPageIds = ref<number[]>([]);
const noteChoice = ref<MergeNoteChoice>('source');

onMounted(async () => {
  candidates.value = await getMergeCandidates(props.sourceContactId);
});

const hasValue = (value: unknown): boolean => value !== null && value !== undefined && String(value).trim() !== '';

const emailOptions = computed(() => preview.value
  ? buildMergeValueOptions(preview.value.sourceContact.emails, preview.value.selectedContact.emails, email => email.email ?? '', normalizeEmail)
  : []);

const phoneNumberOptions = computed(() => preview.value
  ? buildMergeValueOptions(preview.value.sourceContact.phoneNumbers, preview.value.selectedContact.phoneNumbers, phoneNumber => phoneNumber.number ?? '', normalizePhoneNumber)
  : []);

const webPageOptions = computed(() => preview.value
  ? buildMergeValueOptions(preview.value.sourceContact.webPages, preview.value.selectedContact.webPages, webPage => webPage.url ?? '', normalizeUrl)
  : []);

const applyDefaults = (loadedPreview: MergePreview) => {
  const source = loadedPreview.sourceContact;
  const selected = loadedPreview.selectedContact;

  const choices = defaultFieldChoices();
  mergeFieldDefinitions.forEach(({ key, getValue }) => {
    if (key === 'owner') return;
    if (loadedPreview.sourceInvoiceLocked && mergeLockedFieldKeys.includes(key)) return;
    if (!hasValue(getValue(source)) && hasValue(getValue(selected))) {
      choices[key] = 'selected';
    }
  });
  fieldChoices.value = choices;

  addressChoice.value = !loadedPreview.sourceInvoiceLocked && !hasValue(source.addressFormatted) && hasValue(selected.addressFormatted)
    ? 'selected'
    : 'source';

  // With a billing-locked source, its email and phone sets must stay exactly as they are.
  keptEmailIds.value = loadedPreview.sourceInvoiceLocked
    ? emailOptions.value.filter(option => option.origin !== 'selected').map(option => option.id)
    : emailOptions.value.map(option => option.id);
  keptPhoneNumberIds.value = loadedPreview.sourceInvoiceLocked
    ? phoneNumberOptions.value.filter(option => option.origin !== 'selected').map(option => option.id)
    : phoneNumberOptions.value.map(option => option.id);
  keptWebPageIds.value = webPageOptions.value.map(option => option.id);

  const sourceNote = source.notes?.[0]?.note ?? '';
  const selectedNote = selected.notes?.[0]?.note ?? '';
  noteChoice.value = !hasValue(sourceNote) && hasValue(selectedNote) ? 'selected' : 'source';
};

const onSelectContact = async (contactId: number) => {
  if (contactId === props.sourceContactId) {
    showWarning('A contact cannot be combined with itself.', 'Action Not Allowed');
    return;
  }

  const result = await getMergePreview(props.sourceContactId, contactId);
  if (!result) return;

  if (result.selectedLockedForMerge) {
    showWarning(result.lockedReason ?? 'This contact cannot be combined.', 'Action Not Allowed', 8000);
    return;
  }

  preview.value = result;
  applyDefaults(result);
  activeStep.value = 2;
};

const pickFieldValue = (key: MergeFieldKey): string => {
  if (!preview.value) return '';
  const definition = mergeFieldDefinitions.find(field => field.key === key);
  const contact = fieldChoices.value[key] === 'selected' ? preview.value.selectedContact : preview.value.sourceContact;
  return definition?.getValue(contact) ?? '';
};

const survivorName = computed(() => {
  const nameParts = [pickFieldValue('first_name'), pickFieldValue('middle_name'), pickFieldValue('last_name')];
  return nameParts.filter(part => hasValue(part)).join(' ') || 'Unnamed Contact';
});

const mergedSummary = computed<MergeSummary | null>(() => {
  if (!preview.value) return null;

  const source = preview.value.sourceContact;
  const selected = preview.value.selectedContact;
  const sourceNote = source.notes?.[0]?.note ?? '';
  const selectedNote = selected.notes?.[0]?.note ?? '';

  let note = sourceNote;
  if (noteChoice.value === 'selected') {
    note = selectedNote;
  } else if (noteChoice.value === 'combine') {
    note = [sourceNote, selectedNote].filter(text => hasValue(text)).join('\n\n');
  }

  return {
    fullName: survivorName.value,
    fields: mergeFieldDefinitions.map(definition => ({
      key: definition.key,
      label: definition.label,
      value: pickFieldValue(definition.key),
    })),
    address: (addressChoice.value === 'selected' ? selected : source).addressFormatted,
    emails: emailOptions.value.filter(option => keptEmailIds.value.includes(option.id)),
    phoneNumbers: phoneNumberOptions.value.filter(option => keptPhoneNumberIds.value.includes(option.id)),
    webPages: webPageOptions.value.filter(option => keptWebPageIds.value.includes(option.id)),
    note,
  };
});

const newPrimaryBillingAccounts = computed(() => preview.value?.associations.accounts
  .filter(account => account.hasActiveContracts && account.selectedIsPrimary) ?? []);

// True when the current choices would move the source contact's name, address, emails, or phone
// numbers away from what they currently are — the fields an invoice-locked contact would have kept.
const sourceLockedFieldsChanged = computed(() => {
  if (!preview.value) return false;

  const nameFieldsChanged = (['first_name', 'middle_name', 'last_name'] as const)
    .some(key => fieldChoices.value[key] !== 'source');
  if (nameFieldsChanged || addressChoice.value !== 'source') return true;

  const sameIdSet = (a: number[], b: number[]): boolean => {
    const sortedA = [...a].sort((x, y) => x - y);
    const sortedB = [...b].sort((x, y) => x - y);
    return sortedA.length === sortedB.length && sortedA.every((id, index) => id === sortedB[index]);
  };

  const currentSourceEmailIds = emailOptions.value.filter(option => option.origin !== 'selected').map(option => option.id);
  const currentSourcePhoneNumberIds = phoneNumberOptions.value.filter(option => option.origin !== 'selected').map(option => option.id);

  return !sameIdSet(keptEmailIds.value, currentSourceEmailIds) || !sameIdSet(keptPhoneNumberIds.value, currentSourcePhoneNumberIds);
});

const sourceBillingChangeAccounts = computed(() => (sourceLockedFieldsChanged.value ? preview.value?.sourceBillingAccounts ?? [] : []));

const hasBillingChanges = computed(() => newPrimaryBillingAccounts.value.length > 0 || sourceBillingChangeAccounts.value.length > 0);

const submitMerge = async () => {
  if (!preview.value) return;

  const payload: MergePayload = {
    selectedContactId: preview.value.selectedContact.id,
    fieldChoices: fieldChoices.value,
    addressChoice: addressChoice.value,
    keptEmailIds: keptEmailIds.value,
    keptPhoneNumberIds: keptPhoneNumberIds.value,
    keptWebPageIds: keptWebPageIds.value,
    noteChoice: noteChoice.value,
  };

  const result = await mergeContacts(props.sourceContactId, payload);
  if (!result) return;

  (result.errors ?? []).forEach(error => showWarning(error, 'Accounting Sync', 8000));
  emit('merged');
};

const onCombineClick = () => {
  if (!hasBillingChanges.value) {
    submitMerge();
    return;
  }

  const messages: string[] = [];
  if (newPrimaryBillingAccounts.value.length) {
    const accountNames = newPrimaryBillingAccounts.value.map(account => account.c2Name).join(', ');
    messages.push(`${survivorName.value} will become the bill recipient for active contracts on: ${accountNames}.`);
  }
  if (sourceBillingChangeAccounts.value.length) {
    const accountNames = sourceBillingChangeAccounts.value.map(account => account.c2Name).join(', ');
    messages.push(`Billing information (name, address, email, or phone) is changing for active contracts on: ${accountNames}.`);
  }
  messages.push('Are you sure you want to combine these contacts?');

  confirm.require({
    group: 'combineContactsBilling',
    position: 'center',
    icon: 'pi pi-exclamation-triangle',
    header: 'Confirm Billing Change',
    message: messages.join(' '),
    acceptLabel: 'Yes, Combine',
    rejectLabel: 'Cancel',
    rejectClass: 'p-button-secondary',
    acceptProps: {
      dt: greenButtonTheme,
    },
    accept: () => submitMerge(),
  });
};
</script>
<template>
  <FlexScroller>
    <Stepper v-model:value="activeStep" class="px-2 lg:px-4 mt-3 lg:mt-6">
      <StepItem :value="1">
        <Step v-slot="{activateCallback}" as-child>
          <Toolbar align="left" class="py-2">
            <Toolbar
              align="left"
              class="cursor-pointer"
              role="button"
              @click="activateCallback"
            >
              <span class="p-step-number">1</span>
              <Typography variant="stepper-title">
                Select Contact to Combine
              </Typography>
            </Toolbar>
            <Tag v-if="preview" :value="preview.selectedContact.fullName" severity="info" />
          </Toolbar>
        </Step>
        <StepPanel class="my-3.5">
          <MergeCandidateList
            :candidates="candidates"
            :is-loading="isLoadingCandidates || isLoadingPreview"
            :source-contact-name="props.sourceContactName"
            @select="onSelectContact"
          />
        </StepPanel>
      </StepItem>
      <StepItem :value="2">
        <Step v-slot="{activateCallback}" as-child>
          <Toolbar align="left" class="py-2">
            <Toolbar
              align="left"
              :class="preview ? 'cursor-pointer' : 'cursor-not-allowed'"
              role="button"
              @click="() => preview && activateCallback()"
            >
              <span class="p-step-number">2</span>
              <Typography variant="stepper-title">
                Choose Data to Keep
              </Typography>
            </Toolbar>
          </Toolbar>
        </Step>
        <StepPanel class="my-3.5">
          <MergeFieldPicker
            v-if="preview"
            v-model:field-choices="fieldChoices"
            v-model:address-choice="addressChoice"
            v-model:kept-email-ids="keptEmailIds"
            v-model:kept-phone-number-ids="keptPhoneNumberIds"
            v-model:kept-web-page-ids="keptWebPageIds"
            v-model:note-choice="noteChoice"
            :preview="preview"
            :email-options="emailOptions"
            :phone-number-options="phoneNumberOptions"
            :web-page-options="webPageOptions"
          />
          <Typography v-else variant="body">
            Select a contact to combine in step 1 first.
          </Typography>
        </StepPanel>
      </StepItem>
      <StepItem :value="3">
        <Step v-slot="{activateCallback}" as-child>
          <Toolbar align="left" class="py-2">
            <Toolbar
              align="left"
              :class="preview ? 'cursor-pointer' : 'cursor-not-allowed'"
              role="button"
              @click="() => preview && activateCallback()"
            >
              <span class="p-step-number">3</span>
              <Typography variant="stepper-title">
                Review &amp; Confirm
              </Typography>
            </Toolbar>
            <Tag v-if="hasBillingChanges" value="Billing Change" severity="warn" />
          </Toolbar>
        </Step>
        <StepPanel class="my-3.5">
          <MergeReview
            v-if="preview && mergedSummary"
            :preview="preview"
            :summary="mergedSummary"
            :new-primary-billing-accounts="newPrimaryBillingAccounts"
            :source-billing-change-accounts="sourceBillingChangeAccounts"
          />
          <Typography v-else variant="body">
            Select a contact to combine in step 1 first.
          </Typography>
        </StepPanel>
      </StepItem>
    </Stepper>
    <template #footer>
      <SplitBar class="p-2 lg:p-4 border-t border-[var(--p-surface-200)]">
        <template #left>
          <Typography v-if="preview" variant="body">
            Combining Selected Contact into {{ survivorName }}
          </Typography>
        </template>
        <template #right>
          <Toolbar>
            <Button
              severity="secondary"
              label="Cancel"
              :disabled="isMerging"
              @click="emit('close')"
            />
            <Button
              v-if="activeStep > 1"
              severity="secondary"
              label="Back"
              :disabled="isMerging"
              @click="activeStep -= 1"
            />
            <Button
              v-if="activeStep === 2"
              label="Next"
              :disabled="!preview"
              @click="activeStep = 3"
            />
            <Button
              v-if="activeStep === 3"
              :label="isMerging ? 'Combining' : 'Combine Contacts'"
              :icon="isMerging ? 'pi pi-spin pi-spinner' : undefined"
              :disabled="!preview || isMerging"
              :dt="greenButtonTheme"
              @click="onCombineClick"
            />
          </Toolbar>
        </template>
      </SplitBar>
    </template>
  </FlexScroller>
  <ConfirmDialog group="combineContactsBilling" />
</template>
<style scoped>
:deep(.p-steppanel-content) {
  border-left: 2px solid var(--p-stepper-separator-background);
}
</style>
